import type { EngineContext } from "../../context";
import { XMLBuilder } from "fast-xml-parser";
import { resolveIrsIntakeTaxYear, schemaVersionForTaxYear, softwareIdForTaxYear } from "../../constants/taxYear";
import { Form5498SAData } from "./interface/5498-SA-interface";
import { buildSplitRecipientDetail } from "../../constants/recipientIdentity";
import {
  generateRandomRecordId,
  generateUniqueSubmissionId,
  generateUniqueTransmissionId,
} from "../../constants/genRecordId";
import {
  parseAmount,
  stateAndTerritories,
} from "../../constants/validationFunctions";
import {
  buildTransmitterGroup,
  buildVendorGroup,
  buildIssuerDetail,
} from "../../xml/transmitterData";

// Helper function to format amounts for IRS (round to whole dollar)
function formatIrsAmount(amount: string | number): string {
  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;
  // Convert to integer by removing decimal places (round to whole dollar)
  return Math.round(numericAmount).toString();
}

/**
 * Validates and formats a state code to ensure it's valid according to IRS standards
 */
//TODO: REMOVE AFTER DATA RESET
function validateStateCode(stateCode: string | undefined): string {
  const validStates = [
    "AL",
    "AK",
    "AS",
    "AZ",
    "AR",
    "CA",
    "CO",
    "MP",
    "CT",
    "DE",
    "DC",
    "FM",
    "FL",
    "GA",
    "GU",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MH",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PW",
    "PA",
    "PR",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "VI",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
    "AA",
    "AE",
    "AP",
  ];

  if (!stateCode) return "IL"; // Default value

  // Extract first two characters and convert to uppercase
  const code = stateCode.trim().substring(0, 2).toUpperCase();

  // Return the code if valid, otherwise default to 'IL'
  return validStates.includes(code) ? code : "IL";
}

/**
 * Calculates accurate totals for the form group
 * According to the 2025 XSD schema for Form5498SATotalAmtGrpType
 */
function calculateTotals(records: Form5498SAData[]): {
  ArcherMSAContributionAmt: string;
  TotalContributionsAmt: string;
  PriorYearContributionAmt: string;
  RolloverContributionAmt: string;
  EOYFairMarketValueAmt: string;
} {
  // Initialize totals object - order matches 2025 schema
  const totals = {
    ArcherMSAContributionAmt: "0",
    TotalContributionsAmt: "0",
    PriorYearContributionAmt: "0",
    RolloverContributionAmt: "0",
    EOYFairMarketValueAmt: "0",
  };

  // Calculate actual totals from records
  records.forEach((record) => {
    if (record.BOX1_ARCHER_MSA_CONTRIBUTIONS) {
      const amount = parseFloat(
        parseAmount(record.BOX1_ARCHER_MSA_CONTRIBUTIONS)
      );
      totals.ArcherMSAContributionAmt = formatIrsAmount(
        parseFloat(totals.ArcherMSAContributionAmt) + amount
      );
    }

    if (record.BOX2_TOTAL_CONTRIBUTIONS) {
      const amount = parseFloat(parseAmount(record.BOX2_TOTAL_CONTRIBUTIONS));
      totals.TotalContributionsAmt = formatIrsAmount(
        parseFloat(totals.TotalContributionsAmt) + amount
      );
    }

    if (record.BOX3_TOTAL_HSA_ARCHER_CONTRIBUTIONS) {
      const amount = parseFloat(
        parseAmount(record.BOX3_TOTAL_HSA_ARCHER_CONTRIBUTIONS)
      );
      totals.PriorYearContributionAmt = formatIrsAmount(
        parseFloat(totals.PriorYearContributionAmt) + amount
      );
    }

    if (record.BOX4_ROLLOVER_CONTRIBUTIONS) {
      const amount = parseFloat(
        parseAmount(record.BOX4_ROLLOVER_CONTRIBUTIONS)
      );
      totals.RolloverContributionAmt = formatIrsAmount(
        parseFloat(totals.RolloverContributionAmt) + amount
      );
    }

    if (record.BOX5_FAIR_MARKET_VALUE_HSA_ARCHER_MSA_MA_MSA) {
      const amount = parseFloat(
        parseAmount(record.BOX5_FAIR_MARKET_VALUE_HSA_ARCHER_MSA_MA_MSA)
      );
      totals.EOYFairMarketValueAmt = formatIrsAmount(
        parseFloat(totals.EOYFairMarketValueAmt) + amount
      );
    }
  });

  return totals;
}

function isTrue(value: unknown): boolean {
  if (typeof value === "boolean") {
    return value === true;
  }

  if (typeof value === "string") {
    const normalizedValue = value.toLowerCase().trim();
    return (
      normalizedValue === "true" ||
      normalizedValue === "1" ||
      normalizedValue === "yes"
    );
  }

  if (typeof value === "number") {
    return value === 1;
  }

  return false;
}

/**
 * Generate 5498-SA XML that meets IRS requirements
 */
export async function generate5498SAXml(
  ctx: EngineContext,records: Form5498SAData[]) {
  if (!records || !Array.isArray(records) || records.length === 0) {
    throw new Error("No records found or records input is invalid.");
  }

  // Fetch the transmitter data
  const transmitterData = ctx.transmitter as any;

  // Initialize the XML builder
  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: true,
    attributeNamePrefix: "@_",
    processEntities: false,
  });

  // Determine tax year - use 2025 for 2025 schema
  const taxYear = resolveIrsIntakeTaxYear(records[0], ctx.irs.maxIntakeTaxYear);

  // Generate unique record IDs and keep track for uniqueness validation
  const recordIds = new Set<string>();
  const recipientTINs = new Map<string, boolean>();

  // Build form details for each record
  const form5498SADetails = records.map((record) => {
    // Generate and validate unique record ID
    const recordId = record.id.toString();
    if (recordIds.has(recordId)) {
      throw new Error(`Duplicate record ID generated: ${recordId}`);
    }
    recordIds.add(recordId);

    // Track recipient TINs to ensure account numbers for duplicates
    const tin =
      record.BOX_RECIPIENT_ID_NUMBER || record.RECIPIENT_ID_NUMBER || "";
    if (
      recipientTINs.has(tin) &&
      !record.BOX_ACCOUNT_NUMBER &&
      !record.ACCOUNT_NUMBER
    ) {
      console.warn(`Duplicate recipient TIN without account number: ${tin}`);
    }
    recipientTINs.set(tin, true);

    // Validate state code
    const validState = validateStateCode(record.RECIPIENT_STATE);

    // Prepare base data
    const baseData: any = {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsd": "http://www.w3.org/2001/XMLSchema",
      TaxYr: taxYear,
      RecordId: recordId,
      VoidInd: "0",
      CorrectedInd: record.corrected ? "1" : "0",
    };

    // Add PrevSubmittedRecRecipientGrp for corrections
    if (record.corrected) {
      baseData.PrevSubmittedRecRecipientGrp = {
        PrevSubmittedTIN: (
          record.BOX_RECIPIENT_ID_NUMBER ||
          record.RECIPIENT_ID_NUMBER ||
          ""
        ).replace(/-/g, ""),
        PrevSubmittedName: record.RECIPIENT_NAME || "",
      };
    }

    // Add recipient details
    baseData.RecipientDetail = {
      TIN:
        transmitterData.testFileIndicator === "T"
          ? "000000000"
          : (
              record.BOX_RECIPIENT_ID_NUMBER ||
              record.RECIPIENT_ID_NUMBER ||
              ""
            ).replace(/-/g, ""),
      TINSubmittedTypeCd: "INDIVIDUAL_TIN",
      PersonNameControlTxt: (record.RECIPIENT_NAME || "")
        .trim()
        .substring(0, 4)
        .toUpperCase(),
      PersonName: {
        PersonFirstNm: record.RECIPIENT_NAME || "UNKNOWN",
        PersonLastNm: record.RECIPIENT_NAME2 || "UNKNOWN",
      },
    };

    // Add address - handle foreign vs. US
    if (record.FOREIGN_COUNTRY) {
      baseData.RecipientDetail.MailingAddressGrp = {
        ForeignAddress: {
          AddressLine1Txt: record.RECIPIENT_ADDRESS1 || "UNKNOWN_ADDRESS",
          ...(record.RECIPIENT_ADDRESS2 && {
            AddressLine2Txt: record.RECIPIENT_ADDRESS2,
          }),
          ...(record.RECIPIENT_ADDRESS3 && {
            AddressLine3Txt: record.RECIPIENT_ADDRESS3,
          }),
          CityNm: record.RECIPIENT_CITY || "UNKNOWN_CITY",
          CountryCd: record.FOREIGN_COUNTRY.substring(0, 2),
          ...(record.RECIPIENT_ZIP && {
            ForeignPostalCd: record.RECIPIENT_ZIP,
          }),
        },
      };
    } else {
      baseData.RecipientDetail.MailingAddressGrp = {
        USAddress: {
          AddressLine1Txt: record.RECIPIENT_ADDRESS1 || "UNKNOWN_ADDRESS",
          ...(record.RECIPIENT_ADDRESS2 && {
            AddressLine2Txt: record.RECIPIENT_ADDRESS2,
          }),
          CityNm: record.RECIPIENT_CITY || "UNKNOWN_CITY",
          StateAbbreviationCd: validState,
          ZIPCd: record.RECIPIENT_ZIP || "00000",
        },
      };
    }

    // A split-layout record (a Pub 1220 upload, a 2026 CSV with RECIPIENT_FIRST_NAME or
    // RECIPIENT_BUSINESS_NAME1) has no RECIPIENT_NAME, so the legacy shape above filed the
    // payee as UNKNOWN UNKNOWN with an empty name control, which the IRS schema refuses.
    // Such records use the shared builder, as 1099-SA does; legacy records keep the above.
    const splitDetail = buildSplitRecipientDetail(
      record,
      baseData.RecipientDetail.TIN,
      baseData.RecipientDetail.MailingAddressGrp
    );
    if (splitDetail) baseData.RecipientDetail = splitDetail;

    // Add account number if present
    const accountNumber = record.BOX_ACCOUNT_NUMBER || record.ACCOUNT_NUMBER;
    if (accountNumber) {
      baseData.RecipientAccountNum = accountNumber;
    }

    // Add form-specific fields with CORRECT NAMES in the CORRECT ORDER
    baseData.ArcherMSAContributionAmt = formatIrsAmount(
      parseAmount(record.BOX1_ARCHER_MSA_CONTRIBUTIONS || "0")
    );
    baseData.TotalContributionsAmt = formatIrsAmount(
      parseAmount(record.BOX2_TOTAL_CONTRIBUTIONS || "0")
    );
    baseData.PriorYearContributionAmt = formatIrsAmount(
      parseAmount(record.BOX3_TOTAL_HSA_ARCHER_CONTRIBUTIONS || "0")
    );
    baseData.RolloverContributionAmt = formatIrsAmount(
      parseAmount(record.BOX4_ROLLOVER_CONTRIBUTIONS || "0")
    );
    baseData.EOYFairMarketValueAmt = formatIrsAmount(
      parseAmount(record.BOX5_FAIR_MARKET_VALUE_HSA_ARCHER_MSA_MA_MSA || "0")
    );

    // Add indicator fields AFTER amount fields
    baseData.HSAInd = isTrue(record.BOX6_HSA) ? "1" : "0";
    baseData.ArcherMSAInd = isTrue(record.BOX6_ARCHER_MSA) ? "1" : "0";
    baseData.MAMSAInd = isTrue(record.BOX6_MA_MSA) ? "1" : "0";

    return baseData;
  });

  // Calculate accurate totals
  const formTotals = calculateTotals(records);

  // Generate unique identifiers
  const uniqueTransmissionId = generateUniqueTransmissionId(ctx.irs.tcc);
  const submissionId = generateUniqueSubmissionId();

  // Build the complete envelope structure
  const xmlStructure = {
    "?xml": {
      "@_version": "1.0",
      "@_encoding": "UTF-8",
    },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation":
        "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",
      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(taxYear),
        UniqueTransmissionId: uniqueTransmissionId,
        TaxYr: taxYear,
        PriorYearDataInd: "0",
        TransmissionTypeCd: "O",
        TestCd: transmitterData.testFileIndicator || "T",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: transmitterData.vendorIndicator || "V",
        SoftwareId: softwareIdForTaxYear(taxYear, ctx.irs), // First two digits match last two of tax year
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: 1,
        TotalRecipientFormCnt: form5498SADetails.length.toString(),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },
      IRSubmission1Grp: [
        {
          IRSubmission1Header: {
            SubmissionId: submissionId,
            TaxYr: taxYear,
            IssuerDetail: buildIssuerDetail(transmitterData),
            ContactPersonInformationGrp: {
              ContactPersonNm: transmitterData.contactName || "Contact Person",
              ContactPhoneNum: (
                transmitterData.contactTelephoneNumber || "0000000000"
              ).replace(/\D/g, ""),
              ...(transmitterData.contactEmailAddress && {
                ContactEmailAddressTxt: transmitterData.contactEmailAddress,
              }),
              ...(transmitterData.ContactFaxNum && {
                ContactFaxNum: transmitterData.ContactFaxNum.replace(/\D/g, ""),
              }),
            },
            FormTypeCd: "5498SA",
            ParentFormTypeCd: "1096",
            CFSFElectionInd: "0",
            JuratSignatureGrp: {
              SignatureIntentInd: "1",
              JuratSignaturePIN: "00000",
              SignatureDt: new Date().toISOString().split("T")[0], // Current date in YYYY-MM-DD format
              JuratPersonTitleTxt: "CFO",
              PersonNm: transmitterData.contactName || "Officer",
            },
            TotalReportedRcpntFormCnt: form5498SADetails.length.toString(),
            IRSubmission1FormTotals: {
              Form5498SATotalAmtGrp: formTotals,
            },
          },
          IRSubmission1Detail: {
            Form5498SADetail: form5498SADetails,
          },
        },
      ],
    },
  };

  try {
    const xml = builder.build(xmlStructure);
    return { xml, taxYear };
  } catch (error: any) {
    console.error("Error generating XML:", error);
    throw new Error(`Failed to generate XML: ${error.message}`);
  }
}

